import { useState, useEffect, useRef } from 'react'
import { getGlobalNotionsFromAutoTags, getTagForNotionLabel } from '../utils/globalNotions'
import { getComposerStyle } from '../utils/tagGenerator'
import { getComposerDisplayName, getComposerDisplayNameShort } from '../utils/exerciseDisplay'
import {
  CLASSICAL_GENRES,
  CLASSICAL_FORMATIONS,
  HORIZONS_FORMATIONS,
  getFormationTag,
  getGenreTag,
  getFormationLabelFromTag,
  getGenreLabelFromTag
} from '../data/formations'
import { getExerciseDisplayTitle, getExerciseSubtitle, getExerciseGroupRank } from '../utils/exerciseDisplay'
import { getCodexContextLinksForExercise } from '../utils/codexContextLinks'
import { getDisplayDifficulty } from '../utils/difficultyFromContent'
import NotionPillsRow from './NotionPillsRow'
import './ExerciseCard.css'

const STYLE_LABELS = ['Baroque', 'Classique', 'Romantique', 'Moderne']
const MODE_LABELS = ['Majeur', 'Mineur']
const ALL_FORMATIONS = [...CLASSICAL_FORMATIONS, ...HORIZONS_FORMATIONS]

/* Écrans vraiment étroits (téléphone) : pastilles en scroll. Au-dessus : pastilles visibles + tooltip au survol (évite de perdre le tooltip en mode élève quand la console réduit la largeur). */
const MOBILE_BREAKPOINT = 480

function getYouTubeThumbnail(videoId) {
  if (!videoId) return null
  const id = videoId.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/)?.[1] || videoId
  return `https://img.youtube.com/vi/${id}/mqdefault.jpg`
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)
    const handler = () => setIsMobile(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isMobile
}

const TOOLTIP_HIDE_DELAY_MS = 180

function ExerciseCard({
  exercise,
  onClick,
  onPillClick,
  onOpenCodex,
  recommendedLabel,
  recommendedPillPayload,
  variant,
  allExercises,
  videoRank,
  videoGroupSize,
  showDemoTeaserBadge,
}) {
  const isMobile = useIsMobile()
  const useScrollLayout = isMobile // déclaré tôt pour éviter TDZ si utilisé dans un useEffect
  const [showTooltip, setShowTooltip] = useState(false)
  const hideTooltipTimeoutRef = useRef(null)
  const isMini = variant === 'mini'
  // Liens contexte codex : exclure le compositeur car il a déjà sa ligne dédiée (éviter doublon)
  const rawContextLinks = onOpenCodex ? getCodexContextLinksForExercise(exercise) : []
  const contextLinks = rawContextLinks.filter((link) => link.type !== 'composer')

  useEffect(() => {
    return () => {
      if (hideTooltipTimeoutRef.current) clearTimeout(hideTooltipTimeoutRef.current)
    }
  }, [])

  const showRecommended = Boolean(recommendedLabel)
  const isDoneBadge = recommendedLabel === 'Déjà fait'
  const showRecommendedInFooter = showRecommended && !isDoneBadge
  const recommendedPriority = showRecommended && recommendedLabel.includes('objectif')
  const canClickRecommended = Boolean(onPillClick && recommendedPillPayload)
  const recommendedPillProps = canClickRecommended
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: (e) => handlePillClick(e, recommendedPillPayload),
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onPillClick(recommendedPillPayload)
          }
        }
      }
    : {}

  const videoId = exercise.video?.id
  const thumbnailUrl = exercise.thumbnailUrl || getYouTubeThumbnail(videoId)
  const title = Array.isArray(allExercises) && allExercises.length > 0
    ? getExerciseDisplayTitle(exercise, allExercises)
    : (exercise.metadata?.workTitle || exercise.metadata?.exerciseTitle || exercise.metadata?.title || 'Sans titre')
  const subtitle = getExerciseSubtitle(exercise, allExercises || [])
  const groupRankInfo = getExerciseGroupRank(exercise, allExercises || [])
  const useVideoRank = videoRank != null && videoGroupSize != null && videoGroupSize >= 2
  const showIndexBadge = useVideoRank || (Array.isArray(allExercises) && allExercises.length > 0 && groupRankInfo.groupSize > 1)
  const groupRank = useVideoRank ? videoRank : groupRankInfo.rank
  const groupSize = useVideoRank ? videoGroupSize : groupRankInfo.groupSize
  const difficulty = exercise.metadata?.difficulty || null
  const displayDifficulty = difficulty ? getDisplayDifficulty(difficulty) : null
  let notions = getGlobalNotionsFromAutoTags(exercise.autoTags || [])
  // Enrichir avec la période déduite du compositeur si absente des autoTags
  const composerDisplayFull = getComposerDisplayName(exercise.metadata)
  const composerDisplay = isMini ? (getComposerDisplayNameShort(exercise.metadata) || composerDisplayFull) : composerDisplayFull
  if (!notions.some((n) => STYLE_LABELS.includes(n)) && composerDisplayFull) {
    const styleFromComposer = getComposerStyle(composerDisplayFull)
    if (styleFromComposer) notions = [styleFromComposer, ...notions]
  }
  // Fallback mode (Majeur/Mineur) depuis le titre si absent des autoTags (ex. "in D major", "en do mineur")
  if (!notions.some((n) => MODE_LABELS.includes(n))) {
    const titleForMode = [exercise.metadata?.workTitle, exercise.metadata?.exerciseTitle, exercise.metadata?.title].filter(Boolean).join(' ')
    if (/\b(major|majeur)\b/i.test(titleForMode)) notions = [...notions, 'Majeur']
    else if (/\b(minor|mineur)\b/i.test(titleForMode)) notions = [...notions, 'Mineur']
  }
  // Genre : metadata.genre (id) ou premier tag Genre* dans autoTags
  let genreLabel = null
  let genreId = null
  const metaGenre = exercise.metadata?.genre
  if (metaGenre) {
    const g = CLASSICAL_GENRES.find((x) => x.id === metaGenre)
    if (g) {
      genreLabel = g.label
      genreId = g.id
    }
  }
  if (!genreLabel && exercise.autoTags?.length) {
    const genreTag = exercise.autoTags.find((t) => typeof t === 'string' && t.startsWith('Genre'))
    if (genreTag) {
      genreLabel = getGenreLabelFromTag(genreTag)
      const g = CLASSICAL_GENRES.find((x) => getGenreTag(x.id) === genreTag)
      if (g) genreId = g.id
    }
  }
  // Formation : premier id dans metadata.formation ou premier tag Formation* dans autoTags
  let formationLabel = null
  let formationId = null
  const metaFormation = exercise.metadata?.formation
  const firstFormationId = Array.isArray(metaFormation) ? metaFormation[0] : metaFormation
  if (firstFormationId) {
    const f = ALL_FORMATIONS.find((x) => x.id === firstFormationId)
    if (f) {
      formationLabel = f.label
      formationId = f.id
    }
  }
  if (!formationLabel && exercise.autoTags?.length) {
    const formationTag = exercise.autoTags.find((t) => typeof t === 'string' && t.startsWith('Formation'))
    if (formationTag) {
      formationLabel = getFormationLabelFromTag(formationTag)
      const f = ALL_FORMATIONS.find((x) => getFormationTag(x.id) === formationTag)
      if (f) formationId = f.id
    }
  }
  // Ordre d’affichage : difficulté (toujours en premier), puis Style, Mode, Genre, Formation, puis le reste des notions
  const stylePill = notions.find((n) => STYLE_LABELS.includes(n))
  const modePill = notions.find((n) => MODE_LABELS.includes(n))
  const restNotions = notions.filter((n) => !STYLE_LABELS.includes(n) && !MODE_LABELS.includes(n))
  const orderedPillItems = []
  if (displayDifficulty) orderedPillItems.push({ label: displayDifficulty, isDifficulty: true })
  if (stylePill) orderedPillItems.push({ label: stylePill })
  if (modePill) orderedPillItems.push({ label: modePill })
  if (genreLabel && genreId) orderedPillItems.push({ label: genreLabel, payload: { type: 'genre', value: genreId } })
  if (formationLabel && formationId) orderedPillItems.push({ label: formationLabel, payload: { type: 'formation', value: formationId } })
  restNotions.forEach((label) => orderedPillItems.push({ label }))
  const hasMorePills = orderedPillItems.length > 0

  const notionSlug = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\u0300-\u036f/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')

  const handlePillClick = (e, payload) => {
    e.stopPropagation()
    onPillClick(payload)
  }

  const renderPill = (item, index) => {
    const { label, isDifficulty = false, payload } = item
    const hasPayload = Boolean(payload && onPillClick)
    const hasTagFilter = !payload && onPillClick && (isDifficulty || getTagForNotionLabel(exercise.autoTags, label))
    const canFilterPill = hasPayload || hasTagFilter
    const pillPayload = payload || (isDifficulty ? { type: 'difficulty', value: label } : null)
    const tagForNotion = !payload && !isDifficulty ? getTagForNotionLabel(exercise.autoTags, label) : null
    const pillProps = canFilterPill
      ? {
          role: 'button',
          tabIndex: 0,
          onClick: (e) => {
            if (pillPayload) handlePillClick(e, pillPayload)
            else if (tagForNotion) handlePillClick(e, { type: 'tag', value: tagForNotion })
          },
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (pillPayload) onPillClick(pillPayload)
              else if (tagForNotion) onPillClick({ type: 'tag', value: tagForNotion })
            }
          }
        }
      : {}

    if (isDifficulty) {
      return (
        <span
          key="difficulty"
          className={`exercise-card-difficulty ${label}${canFilterPill ? ' exercise-card-pill-clickable' : ''}`}
          {...pillProps}
        >
          {label}
        </span>
      )
    }
    return (
      <span
        key={index}
        className={`exercise-card-pill exercise-card-pill--notion${canFilterPill ? ' exercise-card-pill-clickable' : ''}`}
        data-notion={notionSlug(label)}
        {...pillProps}
      >
        {label}
      </span>
    )
  }

  const showTooltipForMini = isMini && (showRecommended || orderedPillItems.length > 0)

  return (
    <div
      className={`exercise-card${isMini ? ' exercise-card--mini' : ''}`}
      onClick={() => {
        onClick(exercise.id)
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick(exercise.id)
        }
      }}
      aria-label={`Lancer l'exercice ${title}`}
    >
      {isDoneBadge && (
        <span className="exercise-card-badge-top exercise-card-badge-top--done" aria-hidden="true">
          {recommendedLabel}
        </span>
      )}
      <div className="exercise-card-thumbnail">
        {showIndexBadge && (
          <span
            className="exercise-card-index-badge"
            title={`Exercice ${groupRank} sur ${groupSize} (même vidéo)`}
            aria-label={`Exercice ${groupRank} sur ${groupSize}`}
          >
            {groupRank}/{groupSize}
          </span>
        )}
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={title}
            className="exercise-card-thumbnail-img"
            onError={(e) => {
              e.target.style.display = 'none'
              if (e.target.nextElementSibling) {
                e.target.nextElementSibling.style.display = 'flex'
              }
            }}
          />
        ) : null}
        <div
          className="exercise-card-thumbnail-placeholder"
          style={{ display: thumbnailUrl ? 'none' : 'flex' }}
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M9 18V5l12-2v13"></path>
            <circle cx="6" cy="18" r="3"></circle>
            <circle cx="18" cy="16" r="3"></circle>
          </svg>
        </div>
      </div>
      <div className="exercise-card-body">
        <div className="exercise-card-title-row">
          <h3 className="exercise-card-title">{title}</h3>
          {showDemoTeaserBadge ? (
            <span className="exercise-card-demo-badge" title="Exercice vitrine (beta)">Démo</span>
          ) : null}
        </div>
        {subtitle && <p className="exercise-card-subtitle">{subtitle}</p>}
        {composerDisplay && (
          onOpenCodex ? (
            <button
              type="button"
              className="exercise-card-composer exercise-card-composer--link"
              onClick={(e) => {
                e.stopPropagation()
                onOpenCodex('note-compositeurs')
              }}
            >
              {composerDisplay}
            </button>
          ) : (
            <p className="exercise-card-composer">{composerDisplay}</p>
          )
        )}
        {onOpenCodex && contextLinks.length > 0 && (
          <div className="exercise-card-context-links" onClick={(e) => e.stopPropagation()}>
            <span className="exercise-card-context-book-row">
              <span className="exercise-card-context-icon" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
              </span>
              {contextLinks.map((link, idx) => (
                <button
                  key={`ctx-${link.codexEntryId}-${link.sectionId ?? ''}-${idx}`}
                  type="button"
                  className="exercise-card-context-link"
                  onClick={() => onOpenCodex(link.codexEntryId, link.sectionId)}
                >
                  {link.label}
                </button>
              ))}
            </span>
          </div>
        )}
        {(showRecommendedInFooter || orderedPillItems.length > 0) && (
          <div className="exercise-card-footer">
            {useScrollLayout ? (
              <div className="exercise-card-pills exercise-card-pills--scroll">
                {showRecommendedInFooter && (
                  <span className={`exercise-card-pill exercise-card-pill--recommended${recommendedPriority ? ' exercise-card-pill--recommended-priority' : ''}${canClickRecommended ? ' exercise-card-pill-clickable' : ''}`} {...recommendedPillProps}>
                    {recommendedLabel}
                  </span>
                )}
                {orderedPillItems.map((item, i) => renderPill(item, i))}
              </div>
            ) : (
              <div
                className="exercise-card-pills-wrapper"
                onMouseEnter={() => {
                  if (hideTooltipTimeoutRef.current) {
                    clearTimeout(hideTooltipTimeoutRef.current)
                    hideTooltipTimeoutRef.current = null
                  }
                  setShowTooltip(true)
                }}
                onMouseLeave={() => {
                  hideTooltipTimeoutRef.current = setTimeout(() => setShowTooltip(false), TOOLTIP_HIDE_DELAY_MS)
                }}
              >
                <NotionPillsRow
                  items={orderedPillItems}
                  containerClassName="exercise-card-pills exercise-card-pills--visible"
                  moreClassName="exercise-card-pill exercise-card-pill--more"
                  renderPill={renderPill}
                  leading={showRecommendedInFooter ? (
                    <span className={`exercise-card-pill exercise-card-pill--recommended${recommendedPriority ? ' exercise-card-pill--recommended-priority' : ''}${canClickRecommended ? ' exercise-card-pill-clickable' : ''}`} {...recommendedPillProps}>
                      {recommendedLabel}
                    </span>
                  ) : null}
                />
                {((hasMorePills || showTooltipForMini) && showTooltip) && (
                  <div
                    className="exercise-card-pills-tooltip"
                    role="tooltip"
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    onMouseEnter={() => {
                      if (hideTooltipTimeoutRef.current) {
                        clearTimeout(hideTooltipTimeoutRef.current)
                        hideTooltipTimeoutRef.current = null
                      }
                      setShowTooltip(true)
                    }}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    {showRecommendedInFooter && (
                      <span className={`exercise-card-pill exercise-card-pill--recommended${recommendedPriority ? ' exercise-card-pill--recommended-priority' : ''}${canClickRecommended ? ' exercise-card-pill-clickable' : ''}`} {...recommendedPillProps}>
                        {recommendedLabel}
                      </span>
                    )}
                    {orderedPillItems.map((item, i) => renderPill(item, i))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ExerciseCard
